/**
 * @file parseMetric.ts
 * @description
 * Converts a metric data block into district data dictionary
 * keyed by district id.
 *
 * The metric data structure is a single-layer dictionary:
 * - Keys: district IDs in "congress-state-district" format (e.g., "118-ak-01")
 * - Values: metric values as numbers (e.g., 56038 or 10.4537)
 */

import type { DistrictId, DistrictDict } from '@/types';

/**
 * Convert a parsed metric JSON "block" (object keyed by district IDs)
 * into a dictionary (Record) that maps each DistrictId -> DistrictData.
 *
 * The metric data keys are already in "congress-state-district" format (e.g., "118-ak-01"),
 * so we can use them directly as DistrictIds.
 *
 * @param metricBlock - Raw per-district metric values keyed by district IDs
 *   Format: { "118-ak-01": 56038, "118-al-01": 45855, ... }
 * @param metricLabel - The display label for the metric (e.g., "Veteran Count")
 * @returns DistrictDict - Dictionary mapping DistrictId to district data
 */
export function parseMetricToDistrictDict(
  metricBlock: Record<string, number>,
  metricLabel: string,
): DistrictDict {
  const out: DistrictDict = {};

  // Iterate each district entry in the raw block
  for (const [districtId, metricValue] of Object.entries(metricBlock)) {
    // District IDs are already in the correct format (e.g., "118-ak-01")
    const id = districtId as DistrictId;

    // The metric value is directly stored as a number
    // Validate that it's actually a number
    if (typeof metricValue !== 'number' || !Number.isFinite(metricValue)) {
      console.warn(`[parseMetric] Invalid metric value for district ${districtId}: ${metricValue}`);
      continue;
    }

    // Parse district info from the district ID (format: "congress-state-district")
    const parts = districtId.split('-');
    const congressId = parts[0] ? parseInt(parts[0], 10) : undefined;
    const districtNum = parts[2] ? parseInt(parts[2], 10) : undefined;

    out[id] = {
      districtId: id,
      metricLabel: metricLabel,
      metricValue: metricValue,
      districtNum: districtNum,
      congressId: congressId,
    };
  }

  return out;
}
